import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';

// NOTE: LEGAL_NAME must exactly match the entity registered with the IRS and used
// in the Twilio/TCR A2P brand registration. Update before publishing.
const LEGAL_NAME = 'Mickey & Associates, Inc. DBA Replay Museum';
const EFFECTIVE_DATE = 'August 24, 2026';

const SmsTermsPage: React.FC = () => {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center mb-6">
        <MessageSquare size={28} className="text-indigo-600 dark:text-indigo-400 mr-3" />
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Messaging Terms &amp; Conditions</h1>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
        Effective {EFFECTIVE_DATE}
      </p>

      <div className="prose dark:prose-invert max-w-none space-y-6 text-gray-700 dark:text-gray-300">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">The program</h2>
        <p>
          {LEGAL_NAME} sends text messages to people who have asked to hear from us. We use text
          messages for three things:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Replying to a question or a message you left with us.</li>
          <li>Confirming and reminding you about a booking — a party, field trip or group visit.</li>
          <li>Reminders about tournaments, leagues and event nights you asked to be told about.</li>
        </ul>
        <p>
          We do not send promotional or marketing text messages under this program.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">How you opt in</h2>
        <p>
          You opt in by ticking the box on a form on this website, by telling our phone assistant
          it is alright to text you when you leave your number, or by signing up in person at the
          museum. <strong>Consent is never a condition of purchase or admission.</strong>
        </p>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">How often we message</h2>
        <p>
          Message frequency varies. Replies and booking confirmations are sent as needed. Event
          and tournament reminders are typically no more than a few messages per month.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Cost</h2>
        <p>
          <strong>Message and data rates may apply.</strong> We do not charge for text messages,
          but your mobile carrier may.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">How to stop</h2>
        <p>
          Reply <strong>STOP</strong> to any message from us at any time and we will stop texting
          you. You will receive one confirmation that you have been unsubscribed, and then no
          further messages. You can opt back in at any time by contacting us.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">How to get help</h2>
        <p>
          Reply <strong>HELP</strong> to any message, call us on{' '}
          <a href="tel:+17279395093" className="text-indigo-600 dark:text-indigo-400 hover:underline">(727) 939-5093</a>,
          or email{' '}
          <a href="mailto:play@replaymuseum.com" className="text-indigo-600 dark:text-indigo-400 hover:underline">play@replaymuseum.com</a>.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Carriers and delivery</h2>
        <p>
          Mobile carriers are not liable for delayed or undelivered messages. Delivery is not
          guaranteed and may be affected by your carrier or device.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Privacy</h2>
        <p className="font-semibold text-gray-900 dark:text-white">
          We will not share, sell, or rent your mobile phone number or your SMS opt-in consent to
          any third party or affiliate for their own marketing purposes.
        </p>
        <p>
          See our{' '}
          <Link to="/privacy" className="text-indigo-600 dark:text-indigo-400 hover:underline">
            Privacy Policy
          </Link>{' '}
          for how we handle your information.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Contact</h2>
        <p>
          {LEGAL_NAME}<br />
          119 East Tarpon Avenue, Tarpon Springs, FL 34689<br />
          <a href="tel:+17279395093" className="text-indigo-600 dark:text-indigo-400 hover:underline">(727) 939-5093</a><br />
          <a href="mailto:play@replaymuseum.com" className="text-indigo-600 dark:text-indigo-400 hover:underline">play@replaymuseum.com</a>
        </p>
      </div>
    </div>
  );
};

export default SmsTermsPage;
